import { create } from "zustand";
import { persist } from "zustand/middleware";

export const MARKETING_CONSENT_KEY = "thecoach_marketing_consent";
export const MARKETING_CONSENT_EVENT = "thecoach:marketing-consent";

export type MarketingConsent = "granted" | "denied" | null;

interface ConsentStoreState {
  consent: MarketingConsent;
  setConsent: (value: Exclude<MarketingConsent, null>) => void;
  resetConsent: () => void;
}

export const useConsentStore = create<ConsentStoreState>()(
  persist(
    (set) => ({
      // In development mode, default to "granted" so dev tools and flows run freely
      consent: import.meta.env.DEV ? "granted" : null,

      setConsent: (value) => {
        set({ consent: value });
        if (typeof window !== "undefined") {
          try {
            window.dispatchEvent(
              new CustomEvent(MARKETING_CONSENT_EVENT, { detail: value }),
            );
          } catch {
            // Ignore CustomEvent errors
          }
        }
      },

      resetConsent: () => {
        set({ consent: null });
      },
    }),
    {
      name: MARKETING_CONSENT_KEY,
      storage: {
        getItem: (name) => {
          if (typeof window === "undefined") return null;
          try {
            const raw = window.localStorage.getItem(name);
            if (!raw) {
              return import.meta.env.DEV
                ? { state: { consent: "granted" } }
                : null;
            }
            if (raw === "granted" || raw === "denied") {
              return { state: { consent: raw } };
            }
            const parsed = JSON.parse(raw);
            return parsed;
          } catch {
            return import.meta.env.DEV
              ? { state: { consent: "granted" } }
              : null;
          }
        },
        setItem: (name, value) => {
          if (typeof window === "undefined") return;
          try {
            const consentVal = value?.state?.consent;
            if (consentVal) {
              window.localStorage.setItem(name, consentVal);
            }
          } catch {
            // Ignore private browsing or localStorage errors
          }
        },
        removeItem: (name) => {
          if (typeof window === "undefined") return;
          try {
            window.localStorage.removeItem(name);
          } catch {
            // Ignore localStorage removal errors
          }
        },
      },
    },
  ),
);

// Backward-compatible utility functions for non-React contexts
export function readMarketingConsent(): MarketingConsent {
  return useConsentStore.getState().consent;
}

export function saveMarketingConsent(value: Exclude<MarketingConsent, null>) {
  useConsentStore.getState().setConsent(value);
}
